import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, Image, Video, Trash2, Play, Pause } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { privateAlbumService } from '@/services/privateAlbumService';
import { privateMediaService } from '@/services/privateMediaService';
import { usePrivateAlbumLimits } from '@/hooks/usePrivateAlbumLimits';
import { toast } from '@/hooks/use-toast';

interface PrivateAlbumViewerProps {
  album: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limits: any;
  onMediaUpdated: () => void;
}

const PrivateAlbumViewer = ({ 
  album, 
  open, 
  onOpenChange, 
  limits, 
  onMediaUpdated 
}: PrivateAlbumViewerProps) => {
  const { user } = useAuth();
  const { checkCanAddVideoToAlbum } = usePrivateAlbumLimits();
  const [media, setMedia] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [canAddVideo, setCanAddVideo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && album) {
      fetchMedia();
      checkVideoLimit();
    }
  }, [open, album]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const albumMedia = await privateAlbumService.getAlbumMedia(album.id);
      setMedia(albumMedia);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkVideoLimit = async () => {
    const canAdd = await checkCanAddVideoToAlbum(album.id);
    setCanAddVideo(canAdd);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !user) return;

    try {
      setUploading(true);
      
      for (const file of Array.from(files)) {
        if (file.type.startsWith('image/')) {
          await handleImageUpload(file);
        } else if (file.type.startsWith('video/')) {
          if (!canAddVideo) {
            toast({
              title: "Video Limit Reached",
              description: "This album already has the maximum number of videos (1).",
              variant: "destructive"
            });
            continue;
          }
          await handleVideoUpload(file);
        }
      }
      
      await fetchMedia();
      checkVideoLimit();
      onMediaUpdated();
    } catch (error: any) {
      toast({
        title: "Upload Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = async (file: File) => {
    const imageCount = media.filter(m => m.media_type === 'image').length;
    if (imageCount >= 10) {
      throw new Error('Maximum 10 images per album');
    }

    const mediaUrl = await privateMediaService.uploadImage(file, user!.id, album.id);
    await privateAlbumService.addMediaToAlbum(
      album.id,
      mediaUrl,
      'image',
      user!.id,
      file.name,
      file.size
    );
  };

  const handleVideoUpload = async (file: File) => {
    const { url, duration } = await privateMediaService.uploadVideo(file, user!.id, album.id);
    await privateAlbumService.addMediaToAlbum(
      album.id,
      url,
      'video',
      user!.id,
      file.name,
      file.size,
      Math.round(duration)
    );
  };

  const handleDeleteMedia = async (mediaItem: any) => {
    if (!confirm('Are you sure you want to delete this media?')) return;

    try {
      await privateMediaService.deleteMedia(mediaItem.media_url, user!.id);
      await privateAlbumService.deleteMedia(mediaItem.id);
      
      await fetchMedia();
      checkVideoLimit();
      onMediaUpdated();
      
      toast({
        title: "Media Deleted",
        description: "Media has been removed from the album."
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete media",
        variant: "destructive"
      });
    }
  };

  const imageCount = media.filter(m => m.media_type === 'image').length;
  const videoCount = media.filter(m => m.media_type === 'video').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{album.name}</span>
            <div className="text-sm font-normal text-muted-foreground">
              {imageCount}/10 images • {videoCount}/1 video
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Upload Controls */}
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || (imageCount >= 10 && !canAddVideo)}
              variant="outline"
              className="flex-1"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Add Media'}
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Media Grid */}
          <div className="overflow-y-auto max-h-[50vh]">
            {loading ? (
              <div className="text-center py-8">Loading media...</div>
            ) : media.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No media in this album yet</p>
                <p className="text-sm">Upload images and videos to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {media.map((item) => (
                  <div key={item.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                      {item.media_type === 'image' ? (
                        <img
                          src={item.media_url}
                          alt={item.media_name || 'Album media'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <video
                            src={item.media_url}
                            className="w-full h-full object-cover"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <Play className="w-8 h-8 text-white" />
                          </div>
                          {item.duration && (
                            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                              {item.duration}s
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Media type indicator */}
                    <div className="absolute top-2 left-2">
                      {item.media_type === 'image' ? (
                        <Image className="w-4 h-4 text-white drop-shadow-lg" />
                      ) : (
                        <Video className="w-4 h-4 text-white drop-shadow-lg" />
                      )}
                    </div>
                    
                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteMedia(item)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivateAlbumViewer;